import { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import {
  selectRoomID,
  useHMSActions,
  useHMSStore,
  useRecordingStreaming,
} from "@100mslive/react-sdk";
import {
  EndStreamIcon,
  EyeOpenIcon,
  GoLiveIcon,
  InfoIcon,
  LinkIcon,
  PeopleIcon,
  SupportIcon,
  WrenchIcon,
} from "@100mslive/react-icons";
import { Box, Button, Flex, Loading, Text } from "@100mslive/roomkit-react";
import {
  Container,
  ContentBody,
  ContentHeader,
  ErrorText,
  RecordStream,
} from "./Common";
import { useSetAppDataByKey } from "../AppData/useUISettings";
import { useFilteredRoles } from "../../common/hooks";
import { APP_DATA } from "../../common/constants";

const getCardData = (roleName, roomId) => {
  let data = {};
  const formattedRoleName = roleName[0].toUpperCase() + roleName.slice(1);

  switch (roleName) {
    case "broadcaster": {
      data = {
        title: formattedRoleName,
        content:
          // localized in component
          null,
        icon: <SupportIcon />,
      };
      break;
    }
    case "hls-viewer": {
      data = {
        title: i18n.t("lbl_hls_viewer", { defaultValue: "HLS Viewer" }),
        content: null,
        icon: <EyeOpenIcon />,
      };
      break;
    }
    default:
      data = {
        title: formattedRoleName,
        content: i18n.t("msg_role_customised", {
          role: formattedRoleName,
          defaultValue: `${formattedRoleName} is customised with specific permissions, which will determine how it interacts with this room.`,
        }),
        icon: <WrenchIcon />,
        order: 1,
      };
  }
  data["link"] = `/${roomId}/${roleName}`;
  return data;
};

const Card = ({ title, icon, link, content, isHLSRunning, order = 0 }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return isHLSRunning ? (
    <Box
      key={title}
      css={{
        backgroundColor: "$surface_bright",
        padding: "$10",
        order,
        borderRadius: "$2",
      }}
    >
      <Flex align="center" gap="2" css={{ color: "$primary_bright" }}>
        {icon}
        <Text variant="h6" css={{ fontWeight: "$semiBold" }}>
          {title}
        </Text>
      </Flex>
      <Text variant="sm" css={{ color: "$on_surface_medium", mt: "$6" }}>
        {content}
      </Text>
      <Button
        variant="standard"
        onClick={() => {
          navigator.clipboard.writeText(`${window.location.origin}${link}`);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        css={{ w: "100%", r: "$1", mt: "$10", fontWeight: "$semiBold" }}
        icon
      >
        {copied ? (
          <>{t("lbl_link_copied")}</>
        ) : (
          <>
            <LinkIcon style={{ color: "inherit" }} />
            {t("lbl_copy_invite_link")}
          </>
        )}
      </Button>
    </Box>
  ) : null;
};

export const HLSStreaming = ({ onBack }) => {
  const { t } = useTranslation();
  const roleNames = useFilteredRoles();
  const roomId = useHMSStore(selectRoomID);
  const cards = roleNames.map(roleName => getCardData(roleName, roomId));

  const { isHLSRunning } = useRecordingStreaming();
  const [showLinks, setShowLinks] = useState(false);
  return !showLinks ? (
    <Container rounded>
      <ContentHeader
        title={t("lbl_start_streaming")}
        content="HLS"
        onBack={onBack}
      />
      <ContentBody
        title={t("lbl_hls_streaming")}
        Icon={GoLiveIcon}
        removeVerticalPadding
      >
        {t("lbl_hls_streaming_description")}
      </ContentBody>
      {isHLSRunning ? <EndHLS setShowLinks={setShowLinks} /> : <StartHLS />}
    </Container>
  ) : (
    <Container rounded>
      <ContentHeader
        title={t("lbl_invite_people")}
        content={t("lbl_start_conversation")}
        onBack={() => setShowLinks(false)}
      />

      <Flex
        direction="column"
        css={{ gap: "$10", p: "$0 $10", overflowY: "auto", mb: "$10" }}
      >
        {cards.map(card => (
          <Card key={card.title} {...card} isHLSRunning={isHLSRunning} />
        ))}
      </Flex>
    </Container>
  );
};

const StartHLS = () => {
  const { t } = useTranslation();
  const [record, setRecord] = useState(false);
  const [error, setError] = useState(false);
  const hmsActions = useHMSActions();
  const [isHLSStarted, setHLSStarted] = useSetAppDataByKey(APP_DATA.hlsStarted);
  const startHLS = useCallback(
    async variants => {
      try {
        if (isHLSStarted) {
          return;
        }
        setHLSStarted(true);
        setError("");
        await hmsActions.startHLSStreaming({
          variants,
          recording: { hlsVod: record, singleFilePerLayer: record },
        });
      } catch (error) {
        setHLSStarted(false);
        setError(error.message);
      }
    },
    [hmsActions, record, isHLSStarted, setHLSStarted]
  );

  return (
    <Fragment>
      <RecordStream
        record={record}
        setRecord={setRecord}
        testId="hls-recording"
      />
      <Box css={{ p: "$4 $10" }}>
        <ErrorText error={error} />
        <Button
          data-testid="start_hls"
          css={{ w: "100%", r: "$0" }}
          icon
          onClick={() => startHLS()}
          disabled={isHLSStarted}
        >
          {isHLSStarted ? (
            <Loading size={24} color="currentColor" />
          ) : (
            <GoLiveIcon />
          )}
          {isHLSStarted ? t("lbl_starting_stream") : t("lbl_go_live")}
        </Button>
      </Box>
      <Flex align="center" css={{ p: "$4 $10" }}>
        <Text>
          <InfoIcon width={16} height={16} />
        </Text>
        <Text variant="tiny" color="$on_surface_medium" css={{ mx: "$8" }}>
          {t("lbl_cannot_start_recording_once_stream_starts")}
        </Text>
      </Flex>
    </Fragment>
  );
};

const EndHLS = ({ setShowLinks }) => {
  const { t } = useTranslation();
  const hmsActions = useHMSActions();

  const [inProgress, setInProgress] = useState(false);
  const [error, setError] = useState("");
  const { isHLSRunning } = useRecordingStreaming();

  useEffect(() => {
    if (inProgress && !isHLSRunning) {
      setInProgress(false);
    }
  }, [inProgress, isHLSRunning]);

  return (
    <Box css={{ p: "$4 $10" }}>
      <ErrorText error={error} />
      <Button
        data-testid="stop_hls"
        variant="danger"
        css={{ w: "100%", r: "$0", mt: "$8" }}
        icon
        loading={inProgress}
        disabled={inProgress}
        onClick={async () => {
          try {
            setInProgress(true);
            await hmsActions.stopHLSStreaming();
          } catch (error) {
            setError(error.message);
            setInProgress(false);
          }
        }}
      >
        <EndStreamIcon />
        {t("lbl_end_stream", { defaultValue: "End Stream" })}
      </Button>
      <Button
        icon
        css={{ w: "100%", r: "$0", mt: "$8" }}
        onClick={() => setShowLinks(true)}
      >
        <PeopleIcon />{" "}
        {t("lbl_invite_people", { defaultValue: "Invite People" })}
      </Button>
    </Box>
  );
};
